import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {Subscription} from 'rxjs';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {ToastrService} from 'ngx-toastr';
import {OrganizationDetailContext} from './organization-detail.context';

type OrganizationTab = {
    label: string;
    path: string;
    testId: string;
    superUserOnly: boolean;
};

@Component({
    selector: 'app-organization-detail-shell',
    standalone: true,
    imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
    templateUrl: './organization-detail-shell.component.html',
    styleUrl: './organization-detail-shell.component.css',
    providers: [OrganizationDetailContext],
})
export class OrganizationDetailShellComponent implements OnInit, OnDestroy {
    organizationId: string | null = null;
    organizationName = '';
    organizationActive: boolean | null = null;
    loading = false;
    loadError: string | null = null;
    isSuperUser = false;

    readonly tabs: OrganizationTab[] = [
        {label: 'Profile', path: 'profile', testId: 'org-tab-profile', superUserOnly: false},
        {
            label: 'Operational Readiness',
            path: 'operational-readiness',
            testId: 'org-tab-operational-readiness',
            superUserOnly: true,
        },
        {
            label: 'Customer Integrations',
            path: 'customer-integrations',
            testId: 'org-tab-customer-integrations',
            superUserOnly: true,
        },
        {label: 'EEN Account', path: 'een-account', testId: 'org-tab-een-account', superUserOnly: true},
    ];

    private subs = new Subscription();
    private loadSub: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private api: ApiService,
        private auth: AuthService,
        private toastr: ToastrService,
        private organizationContext: OrganizationDetailContext,
    ) {
    }

    get visibleTabs(): OrganizationTab[] {
        return this.tabs.filter((tab) => !tab.superUserOnly || this.isSuperUser);
    }

    ngOnInit(): void {
        this.isSuperUser = this.auth.isSuperUser();
        this.subs.add(
            this.route.paramMap.subscribe((params) => {
                const nextId = params.get('organizationId');
                if (nextId !== this.organizationId) {
                    this.organizationId = nextId;
                    this.loadOrganizationHeader();
                }
            }),
        );
        this.subs.add(
            this.organizationContext.loadError$.subscribe((error) => {
                this.loadError = error;
            }),
        );
    }

    ngOnDestroy(): void {
        this.loadSub?.unsubscribe();
        this.subs.unsubscribe();
        this.organizationContext.clear();
    }

    retryLoadOrganization(): void {
        this.loadOrganizationHeader();
    }

    private loadOrganizationHeader(): void {
        if (!this.organizationId) {
            return;
        }
        this.loadSub?.unsubscribe();
        this.loading = true;
        this.loadError = null;
        this.organizationName = '';
        this.organizationActive = null;

        const expectedId = this.organizationId;
        const generation = this.organizationContext.beginLoad(expectedId);
        const orgPk = Number(expectedId);
        if (!Number.isFinite(orgPk)) {
            this.loading = false;
            this.organizationContext.setLoadError(
                'Unable to load organization.',
                generation,
                expectedId,
            );
            return;
        }

        this.loadSub = this.api.getOrganization(orgPk).subscribe({
            next: (response: any) => {
                if (this.organizationId !== expectedId) {
                    return;
                }
                this.loading = false;
                this.organizationName = response?.name || `Organization #${expectedId}`;
                this.organizationActive = response?.is_active ?? null;
                this.organizationContext.setOrganization(response, generation, expectedId);
            },
            error: (err) => {
                if (this.organizationId !== expectedId) {
                    return;
                }
                this.loading = false;
                this.organizationName = '';
                this.organizationActive = null;
                const message = err?.error?.detail || 'Unable to load organization.';
                this.organizationContext.setLoadError(message, generation, expectedId);
                if (err?.error?.non_field_errors) {
                    err.error.non_field_errors.forEach((element: string) => {
                        this.toastr.error(element, 'Error');
                    });
                } else if (message) {
                    this.toastr.error(message, 'Error');
                }
            },
        });
    }
}
